import React from "react";
import "./styles.css";

export type SkeletonAnimation = "shimmer" | "pulse";

/** Formato do conteúdo espelhado pelo skeleton de modal. */
export type ModalSkeletonVariant = "form" | "grid" | "card";

interface SkeletonProps {
  type?: "table" | "card" | "form" | "custom" | "modal";
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  height?: string | number;
  width?: string | number;
  className?: string;
  modal?: SkeletonModalProps;
}

export const animationClass = (animation: SkeletonAnimation = "shimmer") =>
  animation === "pulse" ? "skeleton-anim-pulse" : "skeleton-anim-shimmer";

export const SkeletonItem: React.FC<{
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
  style?: React.CSSProperties;
}> = ({ width, height, borderRadius, className = "", style }) => {
  return (
    <div
      className={`skeleton-pulse ${className}`}
      style={{
        width: width || "100%",
        height: height || "20px",
        borderRadius: borderRadius,
        ...style,
      }}
    />
  );
};

export const SkeletonTable: React.FC<{
  rows?: number;
  columns?: number;
  showHeader?: boolean;
}> = ({ rows = 5, columns = 5, showHeader = false }) => {
  return (
    <div className="skeleton-table-wrapper">
      {showHeader && (
        <div className="skeleton-table-header">
          {Array.from({ length: columns }).map((_, i) => (
            <SkeletonItem key={`th-${i}`} height={24} className="skeleton-cell" />
          ))}
        </div>
      )}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={`tr-${r}`} className="skeleton-table-row">
          {Array.from({ length: columns }).map((_, c) => (
            <SkeletonItem
              key={`td-${r}-${c}`}
              height={18}
              width={c === 0 ? "70%" : "100%"}
              className="skeleton-cell"
            />
          ))}
        </div>
      ))}
    </div>
  );
};

export const SkeletonCard: React.FC<{ count?: number }> = ({ count = 1 }) => {
  return (
    <div className="flex flex-col gap-4 w-full">
      {Array.from({ length: count }).map((_, i) => (
        <div key={`card-${i}`} className="skeleton-card">
          <div className="flex items-center gap-3">
            <SkeletonItem width={40} height={40} borderRadius="50%" />
            <div className="flex flex-col gap-2 flex-1">
              <SkeletonItem width="60%" height={18} />
              <SkeletonItem width="40%" height={14} />
            </div>
          </div>
          <SkeletonItem width="100%" height={32} />
          <SkeletonItem width="80%" height={14} />
        </div>
      ))}
    </div>
  );
};

export const SkeletonForm: React.FC = () => {
  return (
    <div className="flex flex-col gap-4 p-4 w-full">
      <SkeletonItem width="40%" height={28} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SkeletonItem height={42} borderRadius={8} />
        <SkeletonItem height={42} borderRadius={8} />
        <SkeletonItem height={42} borderRadius={8} />
        <SkeletonItem height={42} borderRadius={8} />
      </div>
      <div className="flex justify-end gap-3 mt-4">
        <SkeletonItem width={100} height={38} borderRadius={20} />
        <SkeletonItem width={120} height={38} borderRadius={20} />
      </div>
    </div>
  );
};

export interface SkeletonModalProps {
  /** Formato do conteúdo real: formulário, grid ou card/detalhe. */
  variant?: ModalSkeletonVariant;
  /** variant="form": nº de campos. variant="card": nº de linhas de texto. */
  fields?: number;
  /** variant="form": colunas do grid de campos. variant="grid": colunas da tabela. */
  columns?: number;
  /** variant="grid": nº de linhas da tabela. */
  rows?: number;
  /** Botões do rodapé. 0 esconde o rodapé. */
  footerButtons?: number;
  showTitle?: boolean;
  showAvatar?: boolean;
  animation?: SkeletonAnimation;
}

/**
 * Skeleton dimensionado para o conteúdo de um modal. Espelha a estrutura real
 * (campos, avatar, linhas, botões de rodapé) em vez de usar a tabela de página
 * inteira do SkeletonTable.
 */
export const SkeletonModal: React.FC<SkeletonModalProps> = ({
  variant = "form",
  fields = 4,
  columns = 2,
  rows = 5,
  footerButtons = 2,
  showTitle = false,
  showAvatar = false,
  animation = "shimmer",
}) => {
  return (
    <div
      className={`skeleton-modal ${animationClass(animation)}`}
      role="status"
      aria-label="Carregando conteúdo"
    >
      <div className="skeleton-modal-body">
        {showTitle && <SkeletonItem width="35%" height={20} />}

        {showAvatar && (
          <div className="skeleton-modal-avatar-row">
            <SkeletonItem width={48} height={48} borderRadius="50%" />
            <div className="skeleton-modal-avatar-lines">
              <SkeletonItem width="55%" height={16} />
              <SkeletonItem width="35%" height={12} />
            </div>
          </div>
        )}

        {variant === "form" && (
          <div
            className="skeleton-modal-grid"
            style={{ "--skeleton-cols": columns } as React.CSSProperties}
          >
            {Array.from({ length: fields }).map((_, i) => (
              <div key={`field-${i}`} className="skeleton-modal-field">
                <SkeletonItem width="42%" height={12} />
                <SkeletonItem width="100%" height={40} borderRadius={8} />
              </div>
            ))}
          </div>
        )}

        {variant === "grid" && (
          <div className="skeleton-modal-datagrid">
            <div className="skeleton-modal-toolbar">
              <SkeletonItem width={180} height={32} borderRadius={8} />
              <SkeletonItem width={120} height={32} borderRadius={999} />
            </div>
            {Array.from({ length: rows }).map((_, r) => (
              <div key={`row-${r}`} className="skeleton-modal-datagrid-row">
                {Array.from({ length: columns }).map((_, c) => (
                  <SkeletonItem
                    key={`cell-${r}-${c}`}
                    height={16}
                    width={c === 0 ? "60%" : "100%"}
                  />
                ))}
              </div>
            ))}
          </div>
        )}

        {variant === "card" && (
          <div className="skeleton-modal-card">
            {Array.from({ length: fields }).map((_, i) => (
              <SkeletonItem
                key={`line-${i}`}
                height={14}
                width={`${Math.max(35, 100 - i * 9)}%`}
              />
            ))}
          </div>
        )}
      </div>

      {footerButtons > 0 && (
        <div className="skeleton-modal-footer">
          {Array.from({ length: footerButtons }).map((_, i) => (
            <SkeletonItem
              key={`btn-${i}`}
              width={96}
              height={36}
              borderRadius={999}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SkeletonLoading: React.FC<SkeletonProps> = ({
  type = "table",
  rows = 6,
  columns = 5,
  showHeader = false,
  height,
  width,
  className = "",
  modal,
}) => {
  if (type === "modal") {
    return <SkeletonModal {...modal} />;
  }

  if (type === "card") {
    return <SkeletonCard count={rows} />;
  }

  if (type === "form") {
    return <SkeletonForm />;
  }

  if (type === "custom") {
    return (
      <SkeletonItem
        height={height}
        width={width}
        className={className}
      />
    );
  }

  return <SkeletonTable rows={rows} columns={columns} showHeader={showHeader} />;
};

export default SkeletonLoading;
